import {
  Button,
  Card,
  Container,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  NoPermissionState,
  Notice,
  PageHeader,
  Segmented,
  Skeleton,
  Toolbar,
} from '@horevia/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BookOpen, CalendarDays, ChevronLeft, ChevronRight, Layers, Plus, Ban } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { CalendarTimeline, type TimelineSelection } from '@/features/calendar/CalendarTimeline';
import { CalendarDayList } from '@/features/calendar/CalendarDayList';
import { CalendarMonthGrid } from '@/features/calendar/CalendarMonthGrid';
import {
  DEFAULT_TIMELINE_RANGE,
  addDays,
  buildDays,
  rangeOf,
  startOfDay,
  type TimelineRange,
} from '@/features/calendar/timeline';
import { BlockFormDrawer } from '@/features/resources/BlockFormDrawer';
import {
  calendarQueryOptions,
  resourceTypesQueryOptions,
  resourcesQueryOptions,
} from '@/features/resources/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { useMediaQuery } from '@/lib/media';
import { useScopedPropertyId } from '@/lib/property';

export const Route = createFileRoute('/_app/calendar')({ component: CalendarPage });

type CalendarView = 'day' | 'week' | 'month' | 'timeline';

/** Nombre de colonnes affichées pour chaque vue (la vue Mois reste en attente). */
const DAYS_BY_VIEW: Record<Exclude<CalendarView, 'month'>, number> = {
  day: 1,
  week: 7,
  timeline: DEFAULT_TIMELINE_RANGE,
};

function CalendarPage() {
  const { t, i18n } = useTranslation(['calendar', 'resources', 'common', 'errors', 'maintenance']);
  const navigate = useNavigate();
  const canRead = useCan('availability.read');
  const canBlock = useCan('availability.block');
  const canCreateReservation = useCan('reservation.create');
  const isMobile = useMediaQuery('(max-width: 767px)');

  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [view, setView] = useState<CalendarView>('timeline');
  const [range, setRange] = useState<TimelineRange>(DEFAULT_TIMELINE_RANGE);
  const [typeCode, setTypeCode] = useState<string | undefined>(undefined);
  const scopedPropertyId = useScopedPropertyId();
  const [selection, setSelection] = useState<TimelineSelection | null>(null);
  const [blockDrawer, setBlockDrawer] = useState<{
    open: boolean;
    preset?: { resourceId?: string; startsAt?: string; endsAt?: string };
  }>({ open: false });

  const dayCount = view === 'timeline' ? range : view === 'month' ? 30 : DAYS_BY_VIEW[view];
  const days = useMemo(() => buildDays(anchor, dayCount), [anchor, dayCount]);
  const { from, to } = useMemo(() => rangeOf(days), [days]);

  const calendarQuery = useQuery({
    ...calendarQueryOptions(from, to, typeCode, scopedPropertyId),
    enabled: canRead,
  });
  const typesQuery = useQuery({ ...resourceTypesQueryOptions(), enabled: canRead });
  const resourcesQuery = useInfiniteQuery({
    ...resourcesQueryOptions({}),
    enabled: canBlock,
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const rows = calendarQuery.data?.rows ?? [];
  const parents = rows.filter((row) => !row.parentResourceId);
  const children = rows.filter((row) => row.parentResourceId);
  const blockableResources = resourcesQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const firstDay = days[0];
  const lastDay = days[days.length - 1];

  const shift = (direction: -1 | 1) =>
    setAnchor((current) => addDays(current, direction * dayCount));

  const openBlockDrawer = (preset: { resourceId?: string; startsAt?: string; endsAt?: string }) => {
    setSelection(null);
    setBlockDrawer({ open: true, preset });
  };

  return (
    <Container>
      <PageHeader
        title={t('calendar:title')}
        subtitle={
          firstDay && lastDay
            ? t('calendar:subtitle', {
                resources: t('resources:countLabel', { count: parents.length }),
                children: t('resources:subCountLabel', { count: children.length }),
                from: formatDate(firstDay.start.toISOString(), i18n.language),
                to: formatDate(addDays(lastDay.start, 0).toISOString(), i18n.language),
              })
            : undefined
        }
        actions={
          <>
            <Segmented<CalendarView>
              label={t('calendar:views.label')}
              value={view}
              onValueChange={setView}
              options={[
                { value: 'day', label: t('calendar:views.day') },
                { value: 'week', label: t('calendar:views.week') },
                { value: 'month', label: t('calendar:views.month') },
                {
                  value: 'timeline',
                  label: t('calendar:views.timeline'),
                  icon: <Layers className="ico ico-14" aria-hidden="true" />,
                },
              ]}
            />
            {canBlock ? (
              <Button variant="primary" onClick={() => openBlockDrawer({})}>
                <Ban className="ico" aria-hidden="true" />
                {t('maintenance:create')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="stack">
        <Card>
          <Toolbar>
            <div className="row" style={{ gap: 4 }}>
              <IconButton label={t('calendar:previous')} onClick={() => shift(-1)}>
                <ChevronLeft className="ico ico-16" aria-hidden="true" />
              </IconButton>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAnchor(startOfDay(new Date()))}
              >
                {t('calendar:today')}
              </Button>
              <IconButton label={t('calendar:next')} onClick={() => shift(1)}>
                <ChevronRight className="ico ico-16" aria-hidden="true" />
              </IconButton>
            </div>

            <Input
              type="date"
              aria-label={t('calendar:datePicker')}
              value={toDateInput(anchor)}
              onChange={(event) => {
                const value = event.target.value;
                if (value) setAnchor(startOfDay(new Date(`${value}T00:00:00`)));
              }}
              style={{ width: 170, height: 30, fontSize: 'var(--fs-13)' }}
            />

            <span className="spacer" />

            {view === 'timeline' ? (
              <Segmented<'7' | '14' | '30'>
                label={t('calendar:ranges.label')}
                value={String(range) as '7' | '14' | '30'}
                onValueChange={(value) => setRange(Number(value) as TimelineRange)}
                options={[
                  { value: '7', label: t('calendar:ranges.7') },
                  { value: '14', label: t('calendar:ranges.14') },
                  { value: '30', label: t('calendar:ranges.30') },
                ]}
              />
            ) : null}

            <label className="sr-only" htmlFor="calendar-type">
              {t('calendar:filters.type')}
            </label>
            <select
              id="calendar-type"
              className="select"
              style={{ width: 'auto', height: 30, fontSize: 'var(--fs-13)' }}
              value={typeCode ?? ''}
              onChange={(event) => setTypeCode(event.target.value || undefined)}
            >
              <option value="">{t('calendar:filters.allTypes')}</option>
              {(typesQuery.data ?? []).map((type) => (
                <option key={type.id} value={type.code}>
                  {type.name}
                </option>
              ))}
            </select>
          </Toolbar>
        </Card>

        <Notice tone="violet" title={t('resources:compositeNotice.title')}>
          <p className="mb-0 t-13">{t('resources:compositeNotice.body')}</p>
        </Notice>

        {calendarQuery.isPending ? (
          <Card>
            <div className="card__body stack">
              <Skeleton variant="line" width="40%" />
              <Skeleton height={56} />
              <Skeleton height={56} />
              <Skeleton height={56} />
              <Skeleton height={56} />
            </div>
          </Card>
        ) : calendarQuery.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(calendarQuery.error, t)}
            traceId={errorTraceId(calendarQuery.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void calendarQuery.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : rows.length === 0 ? (
          <Card>
            <EmptyState
              icon={<CalendarDays className="ico ico-24" aria-hidden="true" />}
              title={t('calendar:emptyTitle')}
              description={t('calendar:emptyBody')}
              action={
                <Button variant="secondary" onClick={() => setTypeCode(undefined)}>
                  {t('common:actions.clearFilters')}
                </Button>
              }
            />
          </Card>
        ) : view === 'month' ? (
          <CalendarMonthGrid
            days={days}
            rows={rows}
            onOpenReservation={(reservationId) =>
              void navigate({ to: '/reservations/$reservationId', params: { reservationId } })
            }
          />
        ) : isMobile && firstDay ? (
          <CalendarDayList day={firstDay} rows={rows} />
        ) : (
          <CalendarTimeline
            days={days}
            rows={rows}
            compact={dayCount >= 30}
            onSelect={(value) => setSelection(value)}
            onCreateBlock={(resourceId) => openBlockDrawer({ resourceId })}
            onOpenResource={(resourceId) =>
              void navigate({ to: '/resources/$resourceId', params: { resourceId } })
            }
            onOpenReservation={(reservationId) =>
              void navigate({ to: '/reservations/$reservationId', params: { reservationId } })
            }
            onCheckIn={(reservationId) =>
              void navigate({ to: '/reservations/$reservationId', params: { reservationId } })
            }
            onConfirm={(reservationId) =>
              void navigate({ to: '/reservations/$reservationId', params: { reservationId } })
            }
          />
        )}
      </div>

      {/* Sélection par glisser : la création de réservation arrive au sprint 2.2. */}
      <Dialog open={selection !== null} onOpenChange={(open) => !open && setSelection(null)}>
        {selection ? (
          <DialogContent
            title={t('calendar:selection.title')}
            description={t('calendar:selection.description', {
              resource: selection.resourceName,
              period: `${formatDate(selection.startsAt, i18n.language)} → ${formatDate(selection.endsAt, i18n.language)}`,
            })}
            icon={<Plus className="ico ico-16" aria-hidden="true" />}
            closeLabel={t('common:actions.close')}
            footer={
              <Button variant="ghost" onClick={() => setSelection(null)}>
                {t('calendar:selection.cancel')}
              </Button>
            }
          >
            <div className="stack stack--sm">
              <Button
                variant="secondary"
                block
                disabled={!canCreateReservation}
                onClick={() =>
                  void navigate({
                    to: '/reservations/new',
                    search: {
                      resourceId: selection.resourceId,
                      from: selection.startsAt,
                      to: selection.endsAt,
                    },
                  })
                }
              >
                <BookOpen className="ico" aria-hidden="true" />
                {t('calendar:selection.newReservation')}
              </Button>
              <Button
                variant="primary"
                block
                disabled={!canBlock}
                onClick={() =>
                  openBlockDrawer({
                    resourceId: selection.resourceId,
                    startsAt: selection.startsAt,
                    endsAt: selection.endsAt,
                  })
                }
              >
                <Ban className="ico" aria-hidden="true" />
                {t('calendar:selection.newBlock')}
              </Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>

      <BlockFormDrawer
        open={blockDrawer.open}
        onOpenChange={(open) => setBlockDrawer((current) => ({ ...current, open }))}
        resources={blockableResources}
        preset={blockDrawer.preset}
      />
    </Container>
  );
}

/** `yyyy-MM-dd` local pour un `<input type="date">`. */
function toDateInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
